/**
 * Environment-driven configuration.
 *
 * Create React App only inlines variables prefixed with `REACT_APP_`, so those
 * are the names used here. Everything in this file ends up in the client
 * bundle — never put a secret in it.
 */

const DEFAULT_API_BASE_URL = 'https://skilzapp.net/api';

const trimTrailingSlashes = value => String(value).replace(/\/+$/, '');

/**
 * Origin + prefix for every admin API call. Defaults to production so existing
 * deployments keep working without a new environment variable.
 */
export const API_BASE_URL = trimTrailingSlashes(
    process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL
);

/**
 * Firebase public web config. These are all publishable keys — they identify
 * the Firebase project to the client SDK and are designed to be shipped in
 * bundles. The Firebase security model relies on Security Rules and the backend
 * (which validates ID tokens), not on keeping these values secret.
 *
 * Required variables (set in `.env.local` or the deployment environment):
 *   REACT_APP_FIREBASE_API_KEY
 *   REACT_APP_FIREBASE_AUTH_DOMAIN
 *   REACT_APP_FIREBASE_PROJECT_ID
 *   REACT_APP_FIREBASE_APP_ID
 *
 * Optional:
 *   REACT_APP_FIREBASE_STORAGE_BUCKET
 *   REACT_APP_FIREBASE_MESSAGING_SENDER_ID
 *   REACT_APP_FIREBASE_MEASUREMENT_ID
 */
export const FIREBASE_CONFIG = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || '',
};

/**
 * Well-known placeholder patterns that look truthy but are not real config.
 *
 * These catch common copy-paste mistakes from .env.example files or deployment
 * templates. The patterns are intentionally broad (case-insensitive, trimmed)
 * to reduce false negatives, but they avoid rejecting any legitimate Firebase
 * value — real API keys start with "AIzaSy", real auth domains end with
 * ".firebaseapp.com" or a custom domain, real project IDs are lowercase
 * alphanumeric with hyphens, and real app IDs follow "1:digits:web:hex".
 */
const PLACEHOLDER_PATTERNS = [
    /^your[-_\s]*/i,         // "your-api-key", "YOUR_PROJECT_ID"
    /^<.*>$/,                // "<paste-here>"
    /^placeholder$/i,
    /^xxx+$/i,               // "xxx", "XXXX"
    /^todo$/i,
    /^change[-_\s]*me$/i,    // "change-me", "CHANGE_ME"
    /^insert[-_\s]*/i,       // "insert-api-key-here"
    /^replace[-_\s]*/i,      // "replace-with-real-value"
    /^example$/i,
    /^none$/i,
    /^n\/a$/i,
    /^test$/i,               // bare "test" (but not "test-project-123")
    /^fake$/i,
    /^dummy$/i,
];

/**
 * Returns true if a config value is a non-empty, non-whitespace, non-placeholder
 * string. Exported for testing.
 */
export const isRealValue = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed.length === 0) return false;
    return !PLACEHOLDER_PATTERNS.some(pattern => pattern.test(trimmed));
};

/**
 * Conservative hostname validation for authDomain.
 *
 * Rejects:
 *   - URL schemes, paths, ports, query strings
 *   - Whitespace anywhere
 *   - Leading or trailing dots (`.example.com`, `example.com.`)
 *   - Consecutive dots (`example..com`)
 *   - Labels starting or ending with hyphens (`-example.com`, `example-.com`)
 *   - Empty labels
 *
 * Accepts ordinary Firebase and custom domains (`my-project.firebaseapp.com`,
 * `auth.my-domain.co.uk`). Does NOT impose a `.firebaseapp.com` suffix.
 *
 * Exported for testing.
 */
export const isValidHostname = (value) => {
    if (typeof value !== 'string') return false;
    const h = value.trim();
    if (h.length === 0) return false;
    // Reject schemes, ports, paths, queries, fragments, whitespace
    if (/[:/?#@\s]/.test(h)) return false;
    // Reject leading or trailing dot
    if (h.startsWith('.') || h.endsWith('.')) return false;
    // Must contain at least one dot
    if (!h.includes('.')) return false;
    // Split into labels and validate each
    const labels = h.split('.');
    for (const label of labels) {
        if (label.length === 0) return false;              // consecutive dots
        if (label.startsWith('-') || label.endsWith('-')) return false;
        if (!/^[a-zA-Z0-9-]+$/.test(label)) return false; // only alnum + hyphen
    }
    return true;
};

/**
 * Structural validation for build-time and runtime checks. Returns an object
 * with `{ valid: boolean, errors: string[] }`. This does NOT verify that the
 * values work against Firebase — only that they look reasonable and are not
 * obvious placeholders or clearly impossible values. Dashboard/runtime validity
 * is still unknown until the Firebase SDK actually connects.
 *
 * Conservative rules (designed never to reject legitimate Firebase config):
 *   - apiKey: starts with "AIzaSy" and is at least 20 chars total
 *     (all known Firebase Web API keys start with this prefix; we do not
 *      enforce an exact length because Google may change key formats)
 *   - authDomain: syntactically valid hostname (see isValidHostname)
 *   - projectId: non-blank, non-placeholder, contains no whitespace or
 *     clearly impossible characters (angle brackets, quotes, backslashes)
 *   - appId: non-blank, non-placeholder, contains no whitespace or
 *     clearly impossible characters
 *
 * Called by scripts/validate-firebase-config.js during `npm run build`,
 * AND used at module load to compute `isFirebaseConfigured` so that runtime
 * and build-time gates are identical (fail-closed).
 */
export const validateFirebaseConfig = (config) => {
    const errors = [];

    // Clearly impossible characters for any Firebase config value
    const IMPOSSIBLE_CHARS = /[<>"'\\{}]/;

    if (!isRealValue(config.apiKey)) {
        errors.push('apiKey: missing or placeholder');
    } else {
        const key = config.apiKey.trim();
        if (!key.startsWith('AIzaSy')) {
            errors.push('apiKey: does not start with "AIzaSy" (all Firebase Web API keys use this prefix)');
        } else if (key.length < 20) {
            errors.push('apiKey: too short to be a valid Firebase Web API key');
        }
    }

    if (!isRealValue(config.authDomain)) {
        errors.push('authDomain: missing or placeholder');
    } else if (!isValidHostname(config.authDomain)) {
        errors.push('authDomain: not a valid hostname (check for schemes, ports, leading/trailing dots, empty labels, or hyphens at label boundaries)');
    }

    if (!isRealValue(config.projectId)) {
        errors.push('projectId: missing or placeholder');
    } else {
        const pid = config.projectId.trim();
        if (/\s/.test(pid) || IMPOSSIBLE_CHARS.test(pid)) {
            errors.push('projectId: contains whitespace or impossible characters');
        }
    }

    if (!isRealValue(config.appId)) {
        errors.push('appId: missing or placeholder');
    } else {
        const aid = config.appId.trim();
        if (/\s/.test(aid) || IMPOSSIBLE_CHARS.test(aid)) {
            errors.push('appId: contains whitespace or impossible characters');
        }
    }

    return { valid: errors.length === 0, errors };
};

/**
 * True when the minimum Firebase config is present AND structurally valid.
 *
 * This derives from `validateFirebaseConfig` so that the runtime gate is
 * identical to the build-time gate — malformed non-placeholder values
 * (e.g. `apiKey: 'not-a-key'`, `authDomain: '.bad'`) are rejected, and
 * `firebase.js` will never call `initializeApp` with them.
 */
export const isFirebaseConfigured = validateFirebaseConfig(FIREBASE_CONFIG).valid;
